import React, { useEffect } from "react"
import { ChevronRightIcon } from "@heroicons/react/24/solid"
import ProductBox from "./ProductBox"
import SeriesBanner from "./SeriesBanner"
import Button from "aldoo-ra/Button"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { useCategories } from "../hooks/useCatalogData"
import { ListProducts, LIST_FIELDS } from "../api"

/**
 * The other books of the series a product belongs to — a series is a
 * taxonomy term of `kind: "series"` the product is filed under, so nothing
 * beyond its categories says which. One row, laid out as the "You may also
 * like" row is (SeeAlsoHorizontal); a product in several series gets their
 * books merged, the innermost series first. `onList(pids)` hears which books
 * the row shows (so another row can leave them out).
 */
export default function MoreFromSeries({ product, className = "", displayCount = 5, onList }) {
  const t = useT()
  const language = useSiteLanguage()
  const { list: categories } = useCategories()
  // the series the product is in — every series term it is filed under, a
  // series and the one it is part of alike, but not the "Series" root, whose
  // parent is no series
  const byId = (id) => categories.find((c) => c.categoryId === id)
  const isSeries = (id) => byId(id)?.kind === "series"
  // a series term proper: one under the "Series" root (whose parent is no series)
  const isTerm = (c) => c?.kind === "series" && (c.parentCategoryIds || []).some(isSeries)
  // the series the product is in, and the series those are part of (a series
  // may sit inside another): the innermost first, each once
  const series = []
  const walk = (c) => {
    if (!isTerm(c) || series.includes(c)) return
    series.push(c)
    for (const parentId of c.parentCategoryIds || []) walk(byId(parentId))
  }
  for (const id of product?.category || []) walk(byId(id))
  const key = series.map((s) => s.categoryId).join(",")

  const { result } = useAsync(
    async () => {
      const pages = await Promise.all(
        series.map((s) => ListProducts({ page: 0, category: s.categoryId, items_per_page: 12, language, owned_only: false, fields: LIST_FIELDS }).catch(() => null))
      )
      const all = pages.flatMap((page) => page?.list || [])
      return all.filter((p, i) => p.pid !== product?.pid && all.findIndex((q) => q.pid === p.pid) === i)
    },
    [key, language, product?.pid],
    { enabled: series.length > 0 }
  )

  const shown = (result || []).slice(0, displayCount)
  const shownKey = shown.map((p) => p.pid).join(",")
  useEffect(() => {
    if (onList) onList(shownKey ? shownKey.split(",") : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey])

  if (!series.length || !result?.length) return null
  // one series: its page is where "See more" goes; several: the first one's
  const seeMore = series[0]
  return (
    <section className={`w-full flex flex-col items-center ${className}`} data-testid="more-from-series">
      <h3 className="text-[24px] md:text-[28px] font-bold text-center m-0">{t`More from the Series`}</h3>
      {/* the whole series in one click, as on the series page (the books not in the cart yet, this one included) */}
      <div className="w-full max-w-w1_xl px-4 md:px-0">
        <SeriesBanner categoryId={seeMore.categoryId} />
      </div>
      <div className="w-full py-10 overflow-x-auto no-scrollbar">
        <div className="flex flex-row flex-nowrap gap-5 w-max mx-auto px-4 md:px-0">
          {shown.map((p) => (
            <ProductBox key={p.pid} product={p} placement="more_from_series" />
          ))}
        </div>
      </div>
      <Button variant="link_accent" className="inline-flex items-center" href={`/category/${seeMore.categoryId}`}>
        {t`See more`}
        <ChevronRightIcon className="w-5 h-5 mt-[1px]" />
      </Button>
    </section>
  )
}
